import { Textarea } from "@/components/ui/Textarea";
import { cn } from "@/lib/utils";
import RadioCircle from "@/components/ui/RadioCircle";
import RoleCardList from "@/components/RoleCardList";
import { OTHER_DEPARTMENT_ID, type Department } from "./types";

type Props = {
  departments: Department[];
  selectedId: string | null;
  otherText: string;
  onSelect: (id: string) => void;
  onOtherTextChange: (value: string) => void;
};

// "What do you do?" — the org's real requestable roles as radio cards (shared
// RoleCardList, same as the admin assign dialog), plus an "I'm not sure /
// Other" escape hatch whose free text becomes the request's role_note.
export default function DepartmentStep({
  departments,
  selectedId,
  otherText,
  onSelect,
  onOtherTextChange,
}: Props) {
  const otherSelected = selectedId === OTHER_DEPARTMENT_ID;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-base font-semibold text-foreground">What do you do?</h2>
        <p className="text-sm text-muted-foreground">
          Pick the role that fits best. An admin sets your exact access when they approve.
        </p>
      </div>

      <RoleCardList roles={departments} selectedId={selectedId} onSelect={onSelect}>
        <button
          type="button"
          aria-pressed={otherSelected}
          onClick={() => onSelect(OTHER_DEPARTMENT_ID)}
          className={cn(
            "flex items-start gap-3 rounded-md border border-dashed px-4 py-3 text-left transition-colors",
            otherSelected ? "border-primary bg-primary/5" : "border-border bg-background hover:bg-muted/50"
          )}
        >
          <span className="mt-0.5">
            <RadioCircle active={otherSelected} />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-medium text-foreground">I'm not sure / Other</span>
            <span className="block text-xs text-muted-foreground">Describe it in your own words.</span>
          </span>
        </button>
      </RoleCardList>

      {otherSelected && (
        <Textarea
          autoFocus
          value={otherText}
          onChange={(e) => onOtherTextChange(e.target.value)}
          placeholder="e.g. Demand planner for TikTok"
          rows={3}
          aria-label="Describe your role"
        />
      )}
    </div>
  );
}
